import { redact } from './redact';

/** Cap on the logged upstream error body, in characters. */
export const MAX_RESPONSE_DATA_LENGTH = 2048;

export const TRUNCATED_SUFFIX = '…[truncated]';

export interface SerializedError {
  name?: string;
  message: string;
  /** Axios error code, e.g. ECONNABORTED. */
  code?: string;
  stack?: string;
  /** HTTP status of the failed response, when there was one. */
  status?: number;
  method?: string;
  url?: string;
  /**
   * The upstream API's error body — the most useful diagnostic field. Redacted
   * and size-capped; see serializeError.
   */
  responseData?: unknown;
  cause?: SerializedError;
}

interface AxiosErrorLike {
  isAxiosError?: boolean;
  name?: string;
  message?: string;
  code?: string;
  stack?: string;
  config?: { method?: string; url?: string; baseURL?: string };
  response?: { status?: number; data?: unknown };
}

/**
 * Reduces an unknown thrown value to a shape that is safe to log.
 *
 * An AxiosError carries the full outbound request config, so logging it raw
 * ships `Cookie` (session id + XSRF token), `X-Booking-Iam-Access-Token` and
 * `x-forwarded-for` to the log platform. We therefore read only
 * the diagnostic fields and never touch `config.headers`, `config.data` (the
 * request body, which carries traveller PII) or `request`.
 *
 * `response.data` IS kept: on a failure it is the upstream API's own error
 * envelope, and it does not contain the request's credentials. It is passed
 * through `redact` (in case a token is echoed back) and truncated.
 *
 * `config.url` is logged as-is: Orval puts query params in `config.params`,
 * which is not logged, so token-bearing requests (GET /v1/guests/invite) expose
 * nothing here, and hand-built query strings carry only search context. Keep it
 * that way — a secret in a query string must be redacted before the url is
 * built. Same for a secret in a path segment: see toSafeErrorInfo in
 * b4b-shortlist-data-access.
 */
export const serializeError = (error: unknown): SerializedError => {
  if (typeof error !== 'object' || error === null) {
    return { message: String(error) };
  }

  const candidate = error as AxiosErrorLike & { cause?: unknown };
  const base: SerializedError = {
    ...(candidate.name && { name: candidate.name }),
    message: candidate.message ?? String(error),
    ...(candidate.stack && { stack: candidate.stack }),
    ...(candidate.cause != null && {
      cause: serializeError(candidate.cause),
    }),
  };

  const { config, response, code } = candidate;

  // Detect structurally rather than trusting `isAxiosError`: an error that
  // carries a `config` or `response` must be reduced whether or not axios
  // tagged it (wrapped errors and hand-rolled rejections reach here too).
  const isAxiosShaped =
    candidate.isAxiosError === true ||
    isObject(config) ||
    isObject(response) ||
    'request' in candidate;

  if (!isAxiosShaped) return base;

  return {
    ...base,
    ...(code && { code }),
    ...(response?.status !== undefined && { status: response.status }),
    ...(config?.method && { method: config.method }),
    ...(config?.url && { url: config.url }),
    ...(response?.data !== undefined && {
      responseData: capResponseData(response.data),
    }),
  };
};

const isObject = (value: unknown) =>
  typeof value === 'object' && value !== null;

const capResponseData = (data: unknown) => {
  if (typeof data === 'string') return truncate(data);

  const redacted = redact(data);
  const serialised = safeStringify(redacted);

  if (serialised === undefined) return '[not serializable]';

  return serialised.length > MAX_RESPONSE_DATA_LENGTH
    ? truncate(serialised)
    : redacted;
};

const truncate = (value: string) =>
  value.length > MAX_RESPONSE_DATA_LENGTH
    ? `${value.slice(0, MAX_RESPONSE_DATA_LENGTH)}${TRUNCATED_SUFFIX}`
    : value;

const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};
